import TRAIcon from "../icons/TRAIcon";
import { cn } from "../utils/cn";
import v from "./traVisual.module.css";
import pf from "./traPlatformForm.module.css";

/** Playo-style input sheet — hairline rows, icon · label · hint · chevron. */
export function TRAFormEntrySheet({ className, children, ...rest }) {
  return (
    <div
      className={cn(v.formEntrySheet, className)}
      data-tra-form-entry-sheet="true"
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * @param {{
 *   icon: string;
 *   label: string;
 *   hint?: string;
 *   onClick?: () => void;
 *   showChevron?: boolean;
 *   active?: boolean;
 *   error?: string;
 *   className?: string;
 *   children?: import("react").ReactNode;
 * }} props
 */
export function TRAFormEntryRow({
  icon,
  label,
  hint,
  onClick,
  showChevron = true,
  active = false,
  error,
  className,
  children,
  ...rest
}) {
  const interactive = Boolean(onClick);
  const RowTag = interactive ? "button" : "div";

  return (
    <div
      className={cn(
        v.formEntryGroup,
        active && v.formEntryGroupActive,
        error && v.formEntryGroupError,
        className
      )}
    >
      <RowTag
        type={interactive ? "button" : undefined}
        className={v.formEntryRow}
        onClick={onClick}
        aria-expanded={interactive ? active : undefined}
        {...rest}
      >
        <span className={v.formEntryIcon} aria-hidden>
          <TRAIcon name={icon} size="md" />
        </span>
        <span className={v.formEntryText}>
          <span className={v.formEntryLabel}>{label}</span>
          {hint ? <span className={v.formEntryHint}>{hint}</span> : null}
          {error ? <span className={v.formEntryError}>{error}</span> : null}
        </span>
        {showChevron && interactive ? (
          <span className={v.formEntryChevron} aria-hidden>
            <TRAIcon name="chevronRight" size="sm" />
          </span>
        ) : null}
      </RowTag>
      {active && children ? (
        <div className={cn(v.formEntryPanel, pf.panel)}>{children}</div>
      ) : null}
    </div>
  );
}

/**
 * @param {{
 *   label: string;
 *   icon?: string;
 *   options: { id: string; label: string; icon?: string }[];
 *   value: string;
 *   onChange: (id: string) => void;
 *   hint?: string;
 * }} props
 */
export function TRASegmentedField({ label, icon, options, value, onChange, hint }) {
  return (
    <div className={v.formSegmentSection}>
      <div className={v.formSegmentHead}>
        {icon ? (
          <span className={v.formEntryIcon} aria-hidden>
            <TRAIcon name={icon} size="md" />
          </span>
        ) : null}
        <span className={v.formSegmentTitle}>{label}</span>
      </div>
      <div className={v.formSegmentTrack} role="group" aria-label={label}>
        {options.map((opt) => {
          const on = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              className={cn(v.formSegmentBtn, on && v.formSegmentBtnOn)}
              aria-pressed={on}
              onClick={() => onChange(opt.id)}
            >
              {opt.icon ? (
                <span className={v.formSegmentBtnIcon} aria-hidden>
                  <TRAIcon name={opt.icon} size="sm" />
                </span>
              ) : null}
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
      {hint ? (
        <p className={v.formInfoHint}>
          <TRAIcon name="info" size="sm" aria-hidden />
          <span>{hint}</span>
        </p>
      ) : null}
    </div>
  );
}

export function TRAFormInfoHint({ className, children }) {
  return (
    <p className={cn(v.formInfoHint, className)}>
      <TRAIcon name="info" size="sm" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

export function TRAFormStickyFooter({ className, children }) {
  return <div className={cn(v.formStickyFooter, className)}>{children}</div>;
}
